import { useEffect, useState } from 'react';
import { api, type DayEntry, type DayReport } from '../lib/api.ts';
import {
  NOMINAL_DAY_MIN, SHIFT_END_MIN, SHIFT_START_MIN,
  formatDuration, formatDecimalHours, formatMinutes, todayInPlant,
} from '../../shared/time.ts';
import { policz } from '../../shared/pl.ts';
import { Blad, Kpi, Ladowanie, Odznaka, Pusto, Tabela, type Kolumna, type Ton } from '../ui/index.tsx';

/**
 * "Mój dzień" — następca arkusza "sprawdz po wpisani !!", który przestał działać.
 * Pracownik ma tu jedną odpowiedź: czy dzień jest rozpisany w całości.
 */
export default function MyDay() {
  const [date, setDate] = useState(todayInPlant());
  const [data, setData] = useState<DayReport | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = () => {
    setErr(null);
    api.get<DayReport>(`/reports/day?date=${date}`).then(setData).catch((e) => setErr(e.message));
  };
  useEffect(load, [date]);
  // Ekran wisi na hali cały dzień — sam się odświeża, żeby nie pokazywać nieaktualnego stanu.
  useEffect(() => {
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [date]);

  if (err) return <Blad tresc={err} ponow={load} />;
  if (!data) return <Ladowanie />;

  const s = data.summary;
  const gapTooBig = s.gapMin > 15;
  const overRegistered = s.gapMin < -15;

  const kolumny: Array<Kolumna<DayEntry>> = [
    { klucz: 'godziny', naglowek: 'Godziny', sort: (e) => e.start_min, szer: '132px',
      render: (e) => (
        <span className="tnum whitespace-nowrap">
          {formatMinutes(e.start_min)}–{formatMinutes(e.end_min)}
          {!!e.is_backdated && <span className="ml-1.5"><Odznaka>wstecz</Odznaka></span>}
        </span>
      ) },
    { klucz: 'operacje', naglowek: 'Operacje',
      render: (e) => (
        <span className="flex flex-wrap gap-1">
          {e.operations.map((o) => <span key={o} className="badge-neutral">{o}</span>)}
          {!!e.is_prototype && <Odznaka ton="warn">prototyp</Odznaka>}
          {!!e.is_finished && <Odznaka ton="ok">zakończone</Odznaka>}
        </span>
      ) },
    { klucz: 'zlecenie', naglowek: 'Zlecenie', sort: (e) => e.zp_number ?? '',
      render: (e) => (
        <span>
          <span className="block whitespace-nowrap">{e.zp_number ?? '—'}</span>
          {e.serials.length > 0 && (
            <span className="block text-caption text-plum-muted tnum">
              {policz(e.serials.length, 'sztuka')}: {e.serials.slice(0, 4).join(', ')}
              {e.serials.length > 4 ? '…' : ''}
            </span>
          )}
        </span>
      ) },
    { klucz: 'netto', naglowek: 'Netto', prawo: true, sort: (e) => e.workedMin, szer: '84px',
      render: (e) => <span className="tnum">{formatDuration(e.workedMin)}</span> },
    { klucz: 'norma', naglowek: 'Norma', prawo: true, sort: (e) => e.creditMin, szer: '84px',
      render: (e) => <span className="tnum font-semibold text-plum">{formatDuration(e.creditMin)}</span> },
    { klucz: 'akcje', naglowek: '', prawo: true, szer: '72px',
      render: (e) => (
        <button
          className="btn-quiet !min-h-0 !py-1 !px-2 text-caption hover:!text-bad"
          onClick={async () => {
            if (!confirm(`Usunąć wpis ${formatMinutes(e.start_min)}–${formatMinutes(e.end_min)}?`)) return;
            await api.del(`/entries/${e.id}`);
            load();
          }}
        >
          Usuń
        </button>
      ) },
  ];

  const tonDnia: Ton = gapTooBig ? 'bad' : overRegistered ? 'warn' : 'ok';
  const tonEf: Ton = s.efficiency === null ? 'neutral'
    : s.efficiency >= 0.9 ? 'ok' : s.efficiency >= 0.7 ? 'warn' : 'bad';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-head-sm">Mój dzień</h1>
        <input type="date" className="field !w-auto tnum" value={date} max={todayInPlant()}
               onChange={(e) => setDate(e.target.value)} aria-label="Dzień" />
        <span className="text-body-sm text-plum-muted">{data.month}, tydzień {data.week}</span>
        <span className="ml-auto text-caption text-plum-muted">dane na {data.plantTime} · czas zakładowy</span>
      </div>

      {/* Główny werdykt dnia */}
      <section className="card p-5">
        <div className="grid sm:grid-cols-3 gap-5">
          <Kpi
            etykieta="Rozpisany czas"
            wartosc={formatDuration(s.registeredMin)}
            pod={`${formatDecimalHours(s.registeredMin)} z ${formatDuration(s.availableMin)} dostępnych`}
            ton={tonDnia}
            udzial={s.availableMin ? s.registeredMin / s.availableMin : 0}
          />
          <Kpi
            etykieta="Wypracowana norma"
            wartosc={formatDuration(s.creditMin)}
            pod={`${formatDecimalHours(s.creditMin)} · ${policz(s.entryCount, 'wpis')}`}
            udzial={s.availableMin ? s.creditMin / s.availableMin : 0}
          />
          <Kpi
            etykieta="Efektywność"
            wartosc={s.efficiency === null ? '—' : `${Math.round(s.efficiency * 100)}%`}
            pod="norma / czas dostępny"
            ton={tonEf}
            udzial={s.efficiency ?? 0}
          />
        </div>

        <div className="mt-5 pt-4 border-t border-line">
          {gapTooBig ? (
            <p className="text-body-sm text-bad">
              <b>Brakuje {formatDuration(s.gapMin)}.</b> Dzień powinien mieć rozpisane {formatDuration(s.availableMin)}
              {s.availableMin > NOMINAL_DAY_MIN ? ' (z nadgodzinami)' : ''}. Uzupełnij brakujące wpisy.
            </p>
          ) : overRegistered ? (
            <p className="text-body-sm text-warn">
              Rozpisano o {formatDuration(-s.gapMin)} za dużo. Sprawdź, czy godziny wpisów się nie nakładają.
            </p>
          ) : (
            <p className="text-body-sm text-ok">Dzień rozpisany w całości.</p>
          )}
          {s.missingStandard.length > 0 && (
            <p className="mt-2 text-body-sm text-warn">
              Brak czasu normatywnego dla {policz(s.missingStandard.length, 'sztuka')}
              {' '}({s.missingStandard.slice(0, 6).join(', ')}{s.missingStandard.length > 6 ? '…' : ''}).
              Efektywność jest zaniżona, dopóki normy nie trafią do aplikacji.
            </p>
          )}
        </div>
      </section>

      <OsZmiany entries={data.entries} />

      <Tabela
        dane={data.entries}
        kolumny={kolumny}
        klucz={(e) => e.id}
        pusto={<Pusto tytul="Brak wpisów w tym dniu"
                      opis="Zarejestruj pracę na zakładce Rejestracja — dzień zostanie rozpisany od razu." />}
      />
    </div>
  );
}

/** Oś zmiany: gdzie w dniu są dziury. Rysowana w czasie zakładowym, nie lokalnym. */
function OsZmiany({ entries }: { entries: Array<{ id: number; start_min: number; end_min: number; is_backdated: number }> }) {
  const from = SHIFT_START_MIN;
  const to = Math.max(SHIFT_END_MIN, ...entries.map((e) => (e.end_min < e.start_min ? e.end_min + 1440 : e.end_min)));
  const span = to - from || 1;
  const hours: number[] = [];
  for (let m = from; m <= to; m += 60) hours.push(m);

  return (
    <section className="card p-4">
      <div className="flex items-center justify-between mb-2.5">
        <span className="text-label uppercase text-plum-muted">Oś zmiany</span>
        <span className="flex items-center gap-3 text-caption text-plum-muted">
          <span className="flex items-center gap-1"><i className="w-2.5 h-2.5 rounded-sm bg-plum inline-block" />praca</span>
          <span className="flex items-center gap-1"><i className="w-2.5 h-2.5 rounded-sm border border-line-strong bg-canvas inline-block" />luka</span>
        </span>
      </div>
      <div className="relative h-7 rounded-sm bg-canvas border border-line overflow-hidden">
        {entries.map((e) => {
          const s = e.start_min;
          const en = e.end_min < e.start_min ? e.end_min + 1440 : e.end_min;
          return (
            <div
              key={e.id}
              title={`${formatMinutes(s)}–${formatMinutes(en)}`}
              className={`absolute inset-y-0 ${e.is_backdated ? 'bg-plum/50' : 'bg-plum'}`}
              style={{ left: `${((s - from) / span) * 100}%`, width: `${Math.max(0.6, ((en - s) / span) * 100)}%` }}
            />
          );
        })}
      </div>
      <div className="flex justify-between mt-1.5 text-caption text-plum-muted tnum">
        {hours.map((h) => <span key={h}>{formatMinutes(h)}</span>)}
      </div>
    </section>
  );
}
